import React from 'react';
import { logError } from '../utils/errorHandling';

/**
 * Error Boundary component to catch JS errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the whole app
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console in development
    logError(error, 'ErrorBoundary');
    
    // Store component stack for debugging
    this.setState({ errorInfo });
    
    // You can also log the error to an error reporting service
    console.log('Component stack trace:', errorInfo.componentStack);
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div className="error-boundary-container" style={{ 
          padding: '20px', 
          margin: '20px', 
          border: '1px solid #f5c6cb',
          borderRadius: '5px', 
          backgroundColor: '#f8d7da', 
          color: '#721c24'
        }}>
          <h2 style={{ marginBottom: '10px' }}>Something went wrong</h2>
          
          {process.env.NODE_ENV === 'development' && (
            <div style={{ 
              marginTop: '15px',
              padding: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              <p><strong>Error:</strong> {this.state.error && this.state.error.toString()}</p>
              
              {this.state.errorInfo && (
                <>
                  <p><strong>Component Stack:</strong></p>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </>
              )}
            </div>
          )}
          
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => window.location.reload()} 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#0070f3', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Reload Page
            </button>
            
            <button 
              onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })} 
              style={{ 
                padding: '8px 16px', 
                backgroundColor: '#28a745', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    // When there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary; 